import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, ShieldCheck, Link2Off, Landmark, ShieldOff, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import PublicPay from "@/components/PublicPay";
import ReportSheet from "@/components/ReportSheet";
import { Logo, LogoMark } from "@/components/Logo";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate } from "@/lib/format";

const REPAY_DISCLAIMER = "Debii never receives or holds your money. Payment is made directly to the creditor.";

const DISPUTE_REASONS = [
  { key: "amount", label: "Amount" },
  { key: "date", label: "Repayment date" },
  { key: "reason", label: "Reason" },
  { key: "unknown", label: "I don't recognise this" },
  { key: "other", label: "Something else" },
];

export default function PublicAck() {
  const { token } = useParams();
  const queryClient = useQueryClient();
  const [view, setView] = useState("main"); // main | dispute | acknowledged | disputed
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [resolved, setResolved] = useState(null); // reported | blocked

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["ack", token],
    queryFn: async () => (await api.get(`/a/${token}`)).data,
    retry: false,
  });

  const acknowledge = async () => {
    setSubmitting(true);
    try {
      await api.post(`/a/${token}/acknowledge`);
      await refetch();
      setView("acknowledged");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDispute = async () => {
    if (!reason) return toast.error("Please choose what looks incorrect.");
    setSubmitting(true);
    try {
      await api.post(`/a/${token}/dispute`, { reason, comment: comment || null });
      setView("disputed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Error / invalid / expired states
  if (error) {
    const code = apiError(error);
    return (
      <PublicWrap>
        <div className="text-center py-10">
          <Link2Off className="mx-auto h-14 w-14 text-muted-foreground" />
          <h1 className="mt-4 font-display text-xl font-bold">
            {code === "expired_link" ? "This link has expired" : "This link isn't valid"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Please ask the person who sent it to share a fresh link.
          </p>
        </div>
      </PublicWrap>
    );
  }

  if (isLoading || !data) {
    return (
      <PublicWrap>
        <div className="flex justify-center py-16"><div className="animate-pop"><LogoMark size={56} /></div></div>
      </PublicWrap>
    );
  }

  // Report / block terminal states take precedence over the active form.
  const isBlocked = resolved === "blocked" || data.blocked;
  const isReported = resolved === "reported" || data.reported;
  if (isBlocked) {
    return (
      <ThankYou
        icon={ShieldOff}
        title="Request blocked"
        subtitle="You've blocked this sender. They can no longer ask you to acknowledge, dispute or repay this request."
        tone="amber"
        testid="ack-blocked"
      />
    );
  }
  if (isReported) {
    return (
      <ThankYou
        icon={Flag}
        title="Report received"
        subtitle="Thanks — our team will review this request. You've already reported it."
        tone="amber"
        testid="ack-reported"
      />
    );
  }

  // Already responded (or just responded)
  if (view === "acknowledged" || (data.already_responded && data.status === "acknowledged" && view === "main")) {
    if (showPay) {
      return <PublicPay basePath={`/a/${token}`} creditor={data.lender_name} onClose={() => setShowPay(false)} />;
    }
    const canPay = data.repayment && data.repayment.available;
    return (
      <ThankYou
        icon={CheckCircle2}
        title="Acknowledged ✓"
        subtitle="Your response has been recorded."
        topAction={canPay ? (
          <div className="mb-4">
            <div className="rounded-3xl bg-card border border-border p-5 text-left">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Outstanding balance</p>
              <p className="font-display text-3xl font-extrabold tabular text-foreground">
                {fmtNaira(data.repayment.outstanding_amount, data.repayment.currency)}
              </p>
              <Button data-testid="ack-pay-debt" onClick={() => setShowPay(true)} className="mt-4 w-full h-12 rounded-xl text-base">
                <Landmark className="h-5 w-5" /> Pay this debt
              </Button>
              <p className="mt-3 text-[11px] text-muted-foreground text-center">{REPAY_DISCLAIMER}</p>
            </div>
          </div>
        ) : null}
      />
    );
  }
  if (view === "disputed" || (data.already_responded && data.status === "disputed" && view === "main")) {
    return <ThankYou icon={AlertTriangle} title="Response recorded" subtitle="We've let them know the details are being questioned." tone="amber" />;
  }

  if (view === "dispute") {
    return (
      <PublicWrap>
        <h1 className="font-display text-xl font-bold text-foreground">What looks incorrect?</h1>
        <div className="mt-4 space-y-2">
          {DISPUTE_REASONS.map((r) => (
            <button
              key={r.key}
              data-testid={`dispute-reason-${r.key}`}
              onClick={() => setReason(r.key)}
              className={`w-full text-left rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                reason === r.key ? "border-primary bg-secondary text-primary" : "border-border bg-white text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <Textarea
          data-testid="dispute-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a short comment (optional)"
          className="mt-3 rounded-xl resize-none"
          rows={3}
        />
        <Button data-testid="dispute-submit" onClick={submitDispute} disabled={submitting} className="mt-4 w-full h-12 rounded-xl">
          {submitting ? "Submitting…" : "Submit"}
        </Button>
        <Button variant="ghost" onClick={() => setView("main")} className="mt-2 w-full h-10 rounded-xl">Back</Button>
      </PublicWrap>
    );
  }

  // Main review page
  const iOwe = data.direction === "i_owe";
  return (
    <PublicWrap>
      <p className="text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">{data.person_name}</span>,{" "}
        {iOwe
          ? <>{data.lender_name} recorded that they owe you this money and asked you to confirm the details.</>
          : <>you've been asked to acknowledge this record.</>}
      </p>

      {data.revised && (
        <div className="mt-3 flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900">
            These details were updated after you questioned them. Please review the latest version below.
          </p>
        </div>
      )}

      <div className="mt-5 rounded-3xl bg-card border border-border p-5">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">Amount</p>
        <p className="font-display text-4xl font-extrabold tabular text-foreground">{fmtNaira(data.amount, data.currency)}</p>
        <div className="mt-4 divide-y divide-border">
          <Row label={iOwe ? "Owed to you by" : "From"} value={data.lender_name} />
          <Row label={iOwe ? "Date borrowed" : "Date lent"} value={fmtDate(data.date_lent)} />
          {data.due_date && <Row label="Expected repayment" value={fmtDate(data.due_date)} />}
          {data.description && <Row label="Reason" value={data.description} />}
        </div>
      </div>

      <div className="mt-5 space-y-2.5">
        <Button data-testid="public-acknowledge-button" onClick={acknowledge} disabled={submitting} className="w-full h-13 h-12 rounded-xl text-base">
          <CheckCircle2 className="h-5 w-5" /> I acknowledge this
        </Button>
        <Button data-testid="public-dispute-button" variant="outline" onClick={() => setView("dispute")} className="w-full h-12 rounded-xl">
          Something isn't correct
        </Button>
      </div>

      <div className="mt-5 flex items-start gap-2 rounded-2xl bg-secondary p-3">
        <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">
          Acknowledging confirms that you've reviewed this record. Debii does not provide loans or guarantee repayment.
        </p>
      </div>

      <button data-testid="public-report-link" onClick={() => setShowReport(true)}
        className="mt-4 w-full text-center text-xs text-muted-foreground underline underline-offset-2">
        Report this request or block the sender
      </button>
      <ReportSheet open={showReport} onOpenChange={setShowReport} basePath={`/a/${token}`} onResolved={setResolved} />
    </PublicWrap>
  );
}

function PublicWrap({ children }) {
  return (
    <div className="debii-shell min-h-screen px-6 py-8">
      <div className="flex justify-center"><Logo size={30} /></div>
      <div className="mt-8 max-w-sm mx-auto w-full animate-rise">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground text-right">{value}</span>
    </div>
  );
}

function ThankYou({ icon: Icon, title, subtitle, tone = "green", topAction = null, testid = null }) {
  return (
    <PublicWrap>
      <div className="text-center py-6" data-testid={testid}>
        <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center animate-pop ${tone === "amber" ? "bg-amber-100" : "bg-emerald-100"}`}>
          <Icon className={`h-11 w-11 ${tone === "amber" ? "text-amber-700" : "text-emerald-700"}`} />
        </div>
        <h1 className="mt-5 font-display text-2xl font-bold text-foreground">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>

        <div className="mt-8">{topAction}</div>

        <div className="mt-4 rounded-3xl bg-primary text-primary-foreground p-6">
          <p className="font-display text-lg font-bold">Keep track of money people owe you too.</p>
          <p className="mt-1 text-sm opacity-80">Debii makes it simple to remember who owes you and when.</p>
          <Button
            data-testid="ack-try-debii"
            onClick={() => (window.location.href = "/welcome")}
            className="mt-4 w-full h-12 rounded-xl bg-white text-primary hover:bg-white/90 text-base"
          >
            Try Debii Free
          </Button>
        </div>
      </div>
    </PublicWrap>
  );
}
